export type DeviceInfo = {
  modelNumber?: string;
  deviceMemory?: number; // (GB)
  platform?: string;
  openglVersion?: string;
  gpuModel?: string;
};

export enum DeviceTier {
  Tier1 = 'Tier1',  // 최대 성능, 모든 스펙 허용
  Tier1_5 = 'Tier1.5',  // __모바일__ 최대 성능, 조경, 단지시설, 건물 디테일 스펙 제한
  Tier2 = 'Tier2',  // 중간 성능, 조경, 단지시설, 건물 디테일 스펙 제한
  Tier3 = 'Tier3',  // 최소 성능, Tier2 퀄리티로 한프레임만 그림
  Unknown = 'Unknown',
}

// 기기 성능별 티어 판정. 기기 모델명을 키값으로 성능 점수 할당.
// 큰 값일수록 높은티어(고사양)이며, 기기 플랫폼 별로 별도의 티어 판정 기준을 가진다.

// 점수 판정 기준 threshold
const ANDROID_TIER1_POINT = 1200;
const ANDROID_TIER2_POINT = 450;
const IPHONE_TIER1_POINT = 1302;
const IPHONE_TIER2_POINT = 1201;
const IPAD_TIER1_POINT = 800;
const IPAD_TIER2_POINT = 700;
const TIER3_POINT = 0;
const UNKNOWN_TIER_POINT = -1;

// androi 기기 성능별 점수 테이블
// 점수는 벤치마크 사이트 참고
// 안드로이드 기기의 모델 식별자는 끝자리 지역 코드를 제외한 값.
// 예) SM-G988N -> SM-G988
const androidTable = new Map<string, number>([
  // Tier 1 (Galaxy S20 이상)
  ['SM-S918', 3724],
  ['SM-S916', 3713],
  ['SM-S911', 3671],
  ['SM-F946', 3366],
  ['SM-F731', 2887],
  ['SM-F936', 2712],
  ['SM-F721', 2712],
  ['SM-S908', 1969],
  ['SM-S906', 1918],
  ['SM-S901', 1830],
  ['SM-G996', 1469],
  ['SM-F926', 1433],
  ['SM-G991', 1420],
  ['SM-G998', 1408],
  ['SM-F711', 1222],
  ['SM-F707', 1220],
  ['SM-N986', 1217],
  ['SM-F916', 1217],
  ['SM-F700', 1216],
  ['SM-N981', 1215],
  ['SM-G988', 1204],
  ['SM-G986', 1203],
  ['SM-G887', 1202],
  ['SM-G981', 1201],
  // Tier 2 threshold (Galaxy S8+ 이상, Galaxy S20FE 이하)
  ['SM-G781', 1066],
  ['SM-G780', 1065],
  ['SM-G977', 1030],
  ['SM-A826', 972],
  ['LM-G900', 886],
  ['LM-V510', 882],
  ['LM-V500', 881],
  ['SM-N971', 879],
  ['SM-G975', 879],
  ['SM-N976', 878],
  ['SM-G973', 878],
  ['SM-G970', 877],
  ['SM-A908', 877],
  ['SM-A546', 813],
  ['SM-A716', 749],
  ['SM-A528', 685],
  ['SM-A346', 639],
  ['SM-A536', 635],
  ['SM-A336', 628],
  ['SM-A516', 627],
  ['SM-A505', 625],
  ['SM-M336', 624],
  ['SM-M536', 609],
  ['SM-N960', 497],
  ['SM-N950', 497],
  ['SM-G965', 497],
  ['SM-G960', 497],
  ['SM-G955', 452],
  // Tier 3 threshold (Galaxy S8 이하)
  ['SM-G950', 407],
  ['SM-A326', 362],
  ['SM-A325', 362],
  ['SM-A315', 357],
  ['SM-A235', 352],
  ['SM-A245', 344],
  ['SM-A426', 332],
  ['SM-M236', 331],
  ['SM-A136', 324],
  ['SM-E426', 321],
  ['SM-A226', 320],
]);

// ios 기기 성능별 점수 테이블
// 모델 identifier 참고(https://gist.github.com/adamawolf/3048717)
// 콤마로 구분값을 가진 숫자를 조합하여 성능 점수를 매기며, 예외적인 경우 직접 포인트를 할당하여 순위 조정
// 예) iPhone13,1 -> 1301, iPad7,1 -> 701
const iPhoneTable = new Map<string, number>([
  // Tier 1 threshold (12 이상)
  ['iPhone13,2', 1302],
  // Tier 2 threshold (11 이상)
  ['iPhone12,1', 1201], // 모델명 규칙으로 환산한 점수보다 실제 성능이 낮아 임의조정(1108 -> 1100)
  // Tier 3 threshold (11 미만)
]);
const iPadTable = new Map<string, number>([
  // Tier 1 threshold (Pro 3rd Gen 이상)
  ['iPad8,1 ', 801],
  // Tier 2 threshold (Pro 2nd Gen 이상)
  ['iPad7,1 ', 701],
  // Tier 3 threshold (6th gen 이하)
  ['iPad7,6 ', 699], // 모델명 규칙으로 환산한 점수보다 실제 성능이 낮아 임의조정(706 -> 699)
  ['iPad7,5 ', 698], // 모델명 규칙으로 환산한 점수보다 실제 성능이 낮아 임의조정(705 -> 698)
])

// 알려지지 않은 ios 기기는 model identifier 넘버링을 조합하여 점수를 판정
const getUnknownIOSDevicePoint = (modelId: string) => {
  try {
    const matches = modelId.match(/\d+/g);
    if (matches) {
        const firstNumber = matches[0];
        const secondNumber = matches[1] ? matches[1].padStart(2, '0') : '00';
        const combinedNumber = Number(firstNumber + secondNumber);
        return combinedNumber;
    } else {
        throw new Error("No numbers found");
    }
  } catch (error: unknown) {
      if (error instanceof Error) {
          console.error("Error occurred:", error.message);
      } else {
          console.error("An unknown error occurred");
      }
  }
  return UNKNOWN_TIER_POINT;
}

// 알려지지 않은 안드로이드 기기는 메모리 크기로 점수를 판정
const getUnknownAndroidDevicePoint = (deviceMemory?: number) => {
  if (!deviceMemory) return UNKNOWN_TIER_POINT;

  if (deviceMemory >= 6.0) return ANDROID_TIER1_POINT;
  else if (deviceMemory >= 3.5) return ANDROID_TIER2_POINT;

  return TIER3_POINT;
}

const determineTier = (point: number, deviceType: 'iPhone' | 'iPad' | 'Android'): DeviceTier => {
  if (point === UNKNOWN_TIER_POINT) return DeviceTier.Unknown;

  switch (deviceType) {
    case 'iPhone':
      if (point >= IPHONE_TIER1_POINT) {
        return DeviceTier.Tier1_5;
      } else if (point >= IPHONE_TIER2_POINT) {
        return DeviceTier.Tier2;
      }
      break;
    case 'iPad':
      if (point >= IPAD_TIER1_POINT) {
        return DeviceTier.Tier1_5;
      } else if (point >= IPAD_TIER2_POINT) {
        return DeviceTier.Tier2;
      }
      break;
    case 'Android':
      if (point >= ANDROID_TIER1_POINT) {
        return DeviceTier.Tier1_5;
      } else if (point >= ANDROID_TIER2_POINT) {
        return DeviceTier.Tier2;
      }
      break;
  }

  return DeviceTier.Tier3;
}

export const checkDeviceTier = (modelNumber?: string, deviceMemory?: number): DeviceTier => {
  if (!modelNumber) return DeviceTier.Unknown;

  let point: number | undefined;
  let deviceType: 'iPhone' | 'iPad' | 'Android'
  let key = modelNumber;

  if (modelNumber.startsWith('iPhone')) {
    point = iPhoneTable.get(key) || getUnknownIOSDevicePoint(modelNumber);
    deviceType = 'iPhone';
  } else if (modelNumber.startsWith('iPad')) {
    point = iPadTable.get(key) || getUnknownIOSDevicePoint(modelNumber);
    deviceType = 'iPad';
  } else {
    // 안드로이드 기기의 경우, 하이픈 이후 4자리까지만(모델명 끝자리 지역 코드를 제외한 값)추출하여 테이블을 조회한다. 예) SM-G988N -> SM-G988
    const hyphenIndex = modelNumber.indexOf('-');
    if (hyphenIndex !== -1) {
      const substringAfterHyphen = modelNumber.substring(hyphenIndex + 1);
      if (substringAfterHyphen.length > 4) {        
        key = modelNumber.substring(0, hyphenIndex + 1) + substringAfterHyphen.substring(0, 4);
      }
    }
    point = androidTable.get(key) || getUnknownAndroidDevicePoint(deviceMemory);
    deviceType = 'Android';
  }

  const tier = determineTier(point, deviceType);
  console.log(`DEVICETYPE: ${deviceType}, MODELID: ${modelNumber}, KEY: ${key} RAM: ${deviceMemory}, TIER: ${tier}, POINT: ${point}`);
  return tier;
}


